/**
 * India-style financial year: April → March.
 * FY labelled by the year in which it starts (e.g. Apr 2025–Mar 2026 = "2025-26").
 */

/** 0-based JS month index for April */
export const FY_START_MONTH_INDEX = 3;

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Column order for FY reports and matrices (April first). */
export const FY_MONTH_ORDER_SHORT = [
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
  "Jan",
  "Feb",
  "Mar",
];

/**
 * Calendar year in which the FY starts (April 1 of that year opens the FY).
 * @param {Date} d
 */
export function getFinancialYearStartYearForDate(d) {
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) {
    return new Date().getFullYear();
  }
  const y = dt.getFullYear();
  const m = dt.getMonth();
  if (m >= FY_START_MONTH_INDEX) return y;
  return y - 1;
}

export function getFinancialYearEndYearForDate(d) {
  return getFinancialYearStartYearForDate(d) + 1;
}

/**
 * Display label: start year + short end year (e.g. 2025 → "2025-26", 2026 → "2026-27").
 * Same style used on vouchers and Indian FY paperwork.
 */
export function formatFinancialYearLabel(startYear) {
  const sy = Number(startYear);
  if (!Number.isFinite(sy)) return "";
  const ey = sy + 1;
  return `${sy}-${String(ey).slice(-2)}`;
}

/**
 * Full end year if you ever need "2025-2026" instead of "2025-26".
 */
export function formatFinancialYearLabelLong(startYear) {
  const sy = Number(startYear);
  if (!Number.isFinite(sy)) return "";
  return `${sy}-${sy + 1}`;
}

/**
 * Dropdown rows for FY pickers (newest first). Labels are "2025-26", "2026-27", …
 * @param {number} selectedStartYear FY April-year currently selected (keeps range inclusive).
 */
export function getFinancialYearDropdownOptions(selectedStartYear) {
  const sel = Math.round(Number(selectedStartYear));
  const startYear = Number.isFinite(sel) ? sel : getCurrentFinancialYearStartYear();
  const cur = getCurrentFinancialYearStartYear();
  const from = Math.min(startYear, cur) - 15;
  const to = Math.max(startYear, cur) + 3;
  const out = [];
  for (let y = to; y >= from; y -= 1) {
    out.push({ startYear: y, label: formatFinancialYearLabel(y) });
  }
  return out;
}

/** Voucher / counter segment e.g. 2025 → "2526" */
export function formatFinancialYearCode(startYear) {
  const sy = Number(startYear);
  const ey = sy + 1;
  return `${String(sy).slice(-2)}${String(ey).slice(-2)}`;
}

export function getFinancialYearCodeForDate(d) {
  return formatFinancialYearCode(getFinancialYearStartYearForDate(d));
}

export function getCurrentFinancialYearStartYear() {
  return getFinancialYearStartYearForDate(new Date());
}

export const APP_FINANCIAL_YEAR_STORAGE_KEY = "erpFinancialYearStart";

/** Menubar FY selection (falls back to today's FY). */
export function readStoredFinancialYearStartYear() {
  try {
    const raw = localStorage.getItem(APP_FINANCIAL_YEAR_STORAGE_KEY);
    if (raw != null && raw !== "" && Number.isFinite(Number(raw))) {
      return Number(raw);
    }
  } catch {
    /* ignore */
  }
  return getCurrentFinancialYearStartYear();
}

export function storeFinancialYearStartYear(startYear) {
  const sy = Math.round(Number(startYear));
  if (!Number.isFinite(sy)) return;
  try {
    localStorage.setItem(APP_FINANCIAL_YEAR_STORAGE_KEY, String(sy));
  } catch {
    /* ignore */
  }
}

/** e.g. 2026 → "01/04/2026 TO 31/03/2027" */
export function formatFinancialYearPeriodRange(startYear) {
  const sy = Number(startYear);
  if (!Number.isFinite(sy)) return "";
  return `01/04/${sy} TO 31/03/${sy + 1}`;
}

/**
 * @param {number} startYear FY start calendar year (April of this year)
 * @returns {Array<{ calendarYear: number, monthIndex: number, fySlot: number }>}
 */
export function getFinancialYearMonthSequence(startYear) {
  const y0 = Number(startYear);
  const out = [];
  for (let slot = 0; slot < 12; slot += 1) {
    if (slot <= 8) {
      out.push({
        calendarYear: y0,
        monthIndex: FY_START_MONTH_INDEX + slot,
        fySlot: slot,
      });
    } else {
      out.push({
        calendarYear: y0 + 1,
        monthIndex: slot - 9,
        fySlot: slot,
      });
    }
  }
  return out;
}

/**
 * FY slot (0 = April of FY start … 11 = March of following calendar year) for a calendar month.
 * @param {number} fyStartYear
 * @param {number} calendarYear
 * @param {number} monthIndex 0–11 (JS Date month)
 */
export function getFYSlotForCalendarMonth(fyStartYear, calendarYear, monthIndex) {
  const y0 = Number(fyStartYear);
  if (!Number.isFinite(y0)) return 0;
  const seq = getFinancialYearMonthSequence(y0);
  const cy = Number(calendarYear);
  const mi = Number(monthIndex);
  const idx = seq.findIndex((s) => s.calendarYear === cy && s.monthIndex === mi);
  return idx >= 0 ? idx : 0;
}

/** FY slot for a calendar date (local). */
export function getFYSlotForCalendarDate(d) {
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return 0;
  const fy = getFinancialYearStartYearForDate(dt);
  return getFYSlotForCalendarMonth(fy, dt.getFullYear(), dt.getMonth());
}

/**
 * Calendar year + month index for Firestore/report queries from FY + slot.
 * @returns {{ calendarYear: number, monthIndex: number, fySlot: number }}
 */
export function getCalendarMonthFromFySlot(fyStartYear, fySlot) {
  const y0 = Number(fyStartYear);
  if (!Number.isFinite(y0)) {
    const now = new Date();
    return {
      calendarYear: now.getFullYear(),
      monthIndex: now.getMonth(),
      fySlot: 0,
    };
  }
  const seq = getFinancialYearMonthSequence(y0);
  const slot = Math.max(0, Math.min(11, Math.floor(Number(fySlot))));
  const spec = seq[slot] || seq[0];
  return {
    calendarYear: spec.calendarYear,
    monthIndex: spec.monthIndex,
    fySlot: spec.fySlot,
  };
}

export function formatMonthShortYear(calendarYear, monthIndex) {
  const m = Math.max(0, Math.min(11, Number(monthIndex)));
  return `${MONTH_SHORT[m]} ${Number(calendarYear)}`;
}

/**
 * Parse invoice-related fields to a local calendar date (noon) for FY rules.
 * @param {unknown} value invoiceDateStr, ISO date string, Date, or Firestore Timestamp-like
 */
export function coerceLocalDateFromInvoiceField(value) {
  if (value == null) return new Date();

  if (typeof value === "object" && typeof value.toDate === "function") {
    const t = value.toDate();
    if (Number.isNaN(t.getTime())) return new Date();
    return new Date(t.getFullYear(), t.getMonth(), t.getDate(), 12, 0, 0, 0);
  }

  if (typeof value === "string") {
    const s = value.trim();
    const m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (m) {
      const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0, 0);
      return Number.isNaN(d.getTime()) ? new Date() : d;
    }
  }

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0, 0);
}

/**
 * Best-effort date from a voucher / invoice payload for FY and counters.
 * @param {object} data
 */
export function getReferenceDateFromInvoicePayload(data) {
  if (!data || typeof data !== "object") return new Date();
  const raw =
    data.invoiceDateStr ??
    data.invoiceDate ??
    data.date ??
    null;
  return coerceLocalDateFromInvoiceField(raw);
}
